import type { Customer, FormFields, SelectedCustomer } from '@/types'
import { getValidAccessToken } from '@/composables/useAuth'

const baseUrl = import.meta.env.VITE_API_URL_PROD as string
const apiKey = import.meta.env.VITE_API_KEY as string

// GET endpoints (customer list, single customer by url_label) are gated by the
// same API_KEY header every other customer-facing endpoint already requires
// (see dispatchService.ts) -- not a login, just "called from our own frontend."
// No driver or the public home-page picker ever needs to authenticate.
const publicHeaders: HeadersInit = { API_KEY: apiKey }

// Mutating admin endpoints (create/delete/saveFields) require the JWT issued
// by pomps-auth-api, attached as a Bearer token -- these are the only calls
// gated behind the actual admin login.
async function adminHeaders(): Promise<HeadersInit> {
    const token = await getValidAccessToken()
    if (!token) throw new Error('Not logged in.')
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    }
}

async function parseError(response: Response, fallback: string): Promise<Error> {
    const body = await response.json().catch(() => null) as { message?: string } | null
    return new Error(body?.message ?? fallback)
}

export const customerService = {
    /**
     * One-time fetch of every customer (used by both the public home-page
     * picker and the admin sidebar's customer list). Replaces the old
     * Firestore onSnapshot-based live subscription -- this is a fetch-on-demand
     * model instead of real-time push, which ers-api doesn't support today.
     * Call again (e.g. after create/delete) to refresh.
     */
    async getAll(): Promise<SelectedCustomer[]> {
        const response = await fetch(`${baseUrl}/customers`, { headers: publicHeaders })
        if (!response.ok) throw await parseError(response, 'Failed to load customers')
        return response.json() as Promise<SelectedCustomer[]>
    },

    async getByUrlLabel(urlLabel: string): Promise<Customer | null> {
        const response = await fetch(`${baseUrl}/customers/${encodeURIComponent(urlLabel)}`, { headers: publicHeaders })
        if (response.status === 404) return null
        if (!response.ok) throw await parseError(response, 'Failed to load customer')
        return response.json() as Promise<Customer>
    },

    async create(customer: Customer): Promise<void> {
        const response = await fetch(`${baseUrl}/customers`, {
            method: 'POST',
            headers: await adminHeaders(),
            body: JSON.stringify({ name: customer.name, urlLabel: customer.url_label }),
        })
        if (!response.ok) throw await parseError(response, 'Failed to create customer')
    },

    async delete(id: string): Promise<void> {
        const response = await fetch(`${baseUrl}/customers/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: await adminHeaders(),
        })
        if (!response.ok) throw await parseError(response, 'Failed to delete customer')
    },

    async saveFields(id: string, fields: FormFields[]): Promise<void> {
        const response = await fetch(`${baseUrl}/customers/${encodeURIComponent(id)}/fields`, {
            method: 'PUT',
            headers: await adminHeaders(),
            body: JSON.stringify({ fields }),
        })
        if (!response.ok) throw await parseError(response, 'Failed to save fields')
    },
}